import { NotificationRecipientKind, TeamsNotificationTemplate } from '@kfolks/types'
import { z } from 'zod'

export const notificationEmailRequestSchema = z.object({
	toAddress: z.email(),
	subject: z.string().min(1),
	templateName: z.string().min(1),
	templateData: z.record(z.string(), z.unknown()),
	notificationType: z.string().min(1),
	notificationVersion: z.string().min(1),
	userId: z.uuid().optional(),
})

export type NotificationEmailRequestInput = z.infer<typeof notificationEmailRequestSchema>

// Teams chat ID format: `19:<hex>@thread.v2` (group chats) OR `19:<oid>_<oid>@unq.gbl.spaces`
// (1:1 chats provisioned by Bot Framework). We accept either with a permissive
// regex on the `19:` prefix to avoid coupling tests to one shape.
const TEAMS_CHAT_ID_REGEX = /^19:[^\s]+$/

export const notificationTeamsRequestedSchema = z
	.object({
		recipient: z.string().min(1),
		recipientKind: z.enum(NotificationRecipientKind),
		template: z.enum(TeamsNotificationTemplate),
		templateData: z.record(z.string(), z.unknown()),
		notificationType: z.string().min(1),
		notificationVersion: z.string().min(1),
		userId: z.uuid().optional(),
		idempotencyKey: z.string().min(1).optional(),
	})
	.superRefine((value, ctx) => {
		if (value.recipientKind === NotificationRecipientKind.USER) {
			const parsed = z.email().safeParse(value.recipient)
			if (!parsed.success) {
				ctx.addIssue({
					code: 'custom',
					path: ['recipient'],
					message: 'recipient must be a valid email when recipientKind is USER',
				})
			}
		} else if (value.recipientKind === NotificationRecipientKind.GROUP_CHAT) {
			if (!TEAMS_CHAT_ID_REGEX.test(value.recipient)) {
				ctx.addIssue({
					code: 'custom',
					path: ['recipient'],
					message: 'recipient must be a Teams chat ID starting with "19:" when recipientKind is GROUP_CHAT',
				})
			}
		}
	})

export type NotificationTeamsRequestInput = z.infer<typeof notificationTeamsRequestedSchema>
